import React, {
    useContext,
    useState,
    useMemo,
    createContext,
    useEffect,
} from "react";

import { useMsal } from "@azure/msal-react";

import GenericUtils from "../utils/GenericUtils";
import HelpService from "../service/Docente/HelpService";
import {
    Document,
    HelpContextData,
    HelpProviderProps,
} from "./interfaces/IHelpContext";
import Features from "../model/dto/Features";
import OptionsSelect from "../model/OptionsSelect";
import { UserRole, getValueTRoleEnum } from "../model/Enums/UserRoleEnum";
import {
    getLabelRoutes,
    getRoutesEnumFromString,
    getValueRoutesByUserRole,
} from "../model/Enums/RoutesEnum";

export const HelpContext = createContext<HelpContextData>(
    {} as HelpContextData
);

export function HelpProvider({ children }: HelpProviderProps) {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [features, setFeatures] = useState<OptionsSelect[]>([]);
    const firstFeature: OptionsSelect = {
        value: "",
        text: "Selecionar funcionalidade",
    };

    const { instance } = useMsal();

    const BLOCKED_FEATURE = 16;

    const getLabelRouteFromMnemonico = (mnemonico: string) => {
        const roleEnum = getValueTRoleEnum(mnemonico) || 0;
        const route = getValueRoutesByUserRole(roleEnum) || "";
        const routeEnum = getRoutesEnumFromString(route);
        return getLabelRoutes(routeEnum) || "";
    };

    const renameActivitiesPlan = (name: string, mnemonico: string) => {
        const label = getLabelRouteFromMnemonico(mnemonico);

        if (label.length) return label;

        return name;
    };

    const getDocuments = async () => {
        setDocuments([]);

        const response = await HelpService.getImportedDocuments(instance);

        if (GenericUtils.isSuccessStatus(response)) {
            const responseDocuments: Document[] = response.data.itens;

            if (responseDocuments.length) {
                const newDocuments = responseDocuments.map((document) => ({
                    ...document,
                    funcionalidade: {
                        ...document.funcionalidade,
                        nome: renameActivitiesPlan(
                            document.funcionalidade.nome,
                            document.funcionalidade.mnemonico
                        ),
                    },
                }));
                setDocuments(newDocuments);
            }
        } else {
            setDocuments([]);
        }
    };

    const getFeatures = async () => {
        if (features.length) return;

        const response = await HelpService.getSystemFeatures(instance);

        if (
            GenericUtils.isSuccessStatus(response) &&
            response.data.itens &&
            response.data.itens.length
        ) {
            const responseFeatures: Features[] = response.data.itens;
            const featuresOptions: OptionsSelect[] = [];

            responseFeatures.forEach(
                (feature) =>
                    feature.id !== BLOCKED_FEATURE &&
                    featuresOptions.push({
                        text: renameActivitiesPlan(
                            feature.nome,
                            feature.mnemonico
                        ),
                        value: feature.id.toString(),
                    })
            );

            let sortedFeaturesOptions = featuresOptions?.sort((a, b) =>
                a.text.localeCompare(b.text)
            );

            sortedFeaturesOptions = [firstFeature, ...sortedFeaturesOptions];

            setFeatures(sortedFeaturesOptions);
        } else {
            setFeatures([]);
        }
    };

    const getCreatedHelp = (featureId: UserRole | null): number => {
        const searchHelp: Document[] = documents.filter(
            (doc) => doc.funcionalidadeId === featureId
        );

        return searchHelp.length ? searchHelp[0].id : 0;
    };

    useEffect(() => {
        getDocuments();
        getFeatures();
    }, []);

    const contextValue = useMemo(
        () => ({
            documents,
            features,
            getDocuments,
            getCreatedHelp,
        }),
        [documents, features]
    );

    return (
        <HelpContext.Provider value={contextValue}>
            {children}
        </HelpContext.Provider>
    );
}

export function useHelp() {
    return useContext(HelpContext);
}
